import React, { useEffect, useRef, useState } from 'react'
import { Typography, InputAdornment, Icon } from '@material-ui/core'
import { TextFieldFormsy } from '@fuse'
import Formsy from 'formsy-react'
import * as authActions from 'app/auth/store/actions'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, useIntl } from 'react-intl'
import ErrorBoundary from 'app/components/ErrorBoundary'

import { ButtonWithLoading } from 'app/components/FormsyHelperComponent'

import AuthenticatedCardHeader from 'app/components/AuthenticateCardHeader'
import TextLinkToDialogButton from 'app/components/buttons/TextLinkToDialogButton'
import * as R from 'ramda'

import {
  errorTypes,
  networkErrorToErrorType,
  networkErrorToMessage,
  statusToErrorType,
} from '../../../../commons/error'

import RegisterDialog from '../RegisterDialog'
import { useAppName } from 'commons/usePartner'
import { openNewTab, termsOfUseUrl, privacyPolicyUrl } from 'commons/website'

export default ({ location, classes, history }) => {
  const dispatch = useDispatch()
  const login = useSelector(({ auth }) => auth.login)
  const appName = useAppName()

  const [isFormValid, setIsFormValid] = useState(false)

  const [formModel, setFormModel] = useState({})
  const [openDialog, setOpenDialog] = useState(false)
  const [emailError, setEmailError] = useState(false)

  const formRef = useRef(null)
  const emailRef = useRef(null)

  const { formatMessage } = useIntl()

  useEffect(() => {
    if (login.error && (login.error.email || login.error.password)) {
      formRef.current.updateInputsWithError({
        ...login.error,
      })
      disableButton()
    }
  }, [login.error])
  function disableButton() {
    setIsFormValid(false)
  }
  function enableButton() {
    setIsFormValid(true)
  }

  const handleError = (next, error) => {
    switch (networkErrorToErrorType(error)) {
      case statusToErrorType(400, 603):
      case statusToErrorType(400, 605): {
        const message = R.prop(
          'message',
          networkErrorToMessage(formatMessage)(error, errorTypes.register),
        )
        formRef.current.updateInputsWithError({
          email: message,
        })
        emailRef.current.focus()
        break
      }
      default:
        next()
    }
  }

  const token = window.location.search
    .slice(1)
    .split('&')
    .map((row) => row.split('='))
    .find((row) => row[0] === 'token')
  const placeId = window.location.search
    .slice(1)
    .split('&')
    .map((row) => row.split('='))
    .find((row) => row[0] === 'placeId')[1]
  const mail = window.location.search
    .slice(1)
    .split('&')
    .map((row) => row.split('='))
    .find((row) => row[0] === 'mail')[1]

  const onSuccess = (model) => {
    const registerData = {
      placeId,
      ...model,
    }
    if (token && token.length > 1) {
      registerData.token = token[1]
    }
    dispatch(
      authActions.submitCuratedRegister(
        registerData,
        formatMessage,
        handleError,
        history,
      ),
    )
  }

  function handleSubmit(model) {
    const newModel = {
      ...model,
      email: mail.trim(),
    }

    const onProceedWithCaution = () => {
      setFormModel(newModel)
      setOpenDialog(true)
    }

    !login.error.email ? setEmailError(true) : setEmailError(false)

    dispatch(
      authActions.checkEmail(
        {
          email: newModel.email,
          onSuccess: () => onSuccess(newModel),
          onProceedWithCaution: onProceedWithCaution,
        },
        formatMessage,
        handleError,
      ),
    )
  }

  const onAgreeUponProceedWithCaution = () => onSuccess(formModel)

  return (
    <div className="w-full sm:py-36 sm:px-64 py-24 px-16">
      <AuthenticatedCardHeader
        title={formatMessage(
          { id: 'registerForm.subtitle1' },
          {
            appName,
            b: (text) => (
              <span className="font-bold" key={text}>
                {text}
              </span>
            ),
          },
        )}
        subtitle={formatMessage({ id: 'login.introduction' })}
      />

      <div className="flex flex-col items-center w-full">
        <ErrorBoundary
          fallback={
            <div className="flex flex-col p-8 rounded shadow-lg">
              <span className="font-bold">
                <FormattedMessage id="error.general" />
              </span>
            </div>
          }
        >
          <Formsy
            onValidSubmit={handleSubmit}
            onValid={enableButton}
            onInvalid={disableButton}
            ref={formRef}
            className="flex flex-col justify-center w-full xs:w-xs md:w-2/3 lg:w-1/3"
          >
            <TextFieldFormsy
              className="mb-16"
              type="password"
              name="password"
              label={formatMessage({
                id: 'registerForm.passwordPlaceholder',
              })}
              value=""
              validations={{
                minLength: 6,
              }}
              validationErrors={{
                minLength: formatMessage({
                  id: 'login.textField.validation.minLengthPassword',
                }),
              }}
              inputProps={{
                'data-testid': 'password',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon className="text-20" color="action">
                      vpn_key
                    </Icon>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              required
            />

            <TextFieldFormsy
              className="mb-16"
              type="password"
              name="confirmPassword"
              label={formatMessage({
                id: 'registerForm.confirmPasswordPlaceholder',
              })}
              value=""
              validations={{
                equalsField: 'password',
              }}
              validationErrors={{
                equalsField: formatMessage({
                  id: 'registerForm.confirmPasswordInvalid',
                }),
              }}
              inputProps={{
                'data-testid': 'password',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon className="text-20" color="action">
                      vpn_key
                    </Icon>
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              required
            />

            <ButtonWithLoading
              type="submit"
              variant="contained"
              color="secondary"
              className="w-full mx-auto mt-16 uppercase"
              aria-label="LOG IN"
              disabled={!isFormValid || login.isSubmitting}
              isLoading={login.isSubmitting}
              value="legacy"
              CircularProp={{ color: 'primary' }}
              data-testid="submit"
            >
              <FormattedMessage id="login.createAccountTwoPart" />
            </ButtonWithLoading>
          </Formsy>
          {emailError && (
            <div className="flex text-center p-2" style={{ color: '#f44336' }}>
              <FormattedMessage id="error.400.emailExist" />
            </div>
          )}
        </ErrorBoundary>

        <div className="flex text-center">
          <Typography className="mt-20">
            <FormattedMessage
              id="login.acceptTermAndPolicySentence"
              values={{
                termOfService: (
                  <TextLinkToDialogButton
                    text={<FormattedMessage id="login.termsOfService" />}
                    onClick={() => openNewTab(termsOfUseUrl)}
                    data-testid="termOfService"
                  />
                ),
                privacyPolicy: (
                  <TextLinkToDialogButton
                    text={<FormattedMessage id="login.privacyPolicy" />}
                    onClick={() => openNewTab(privacyPolicyUrl)}
                    data-testid="privacyPolicy"
                  />
                ),
              }}
            />
          </Typography>
        </div>
        <RegisterDialog
          email={formModel.email}
          open={openDialog}
          setOpen={setOpenDialog}
          onAgree={onAgreeUponProceedWithCaution}
          emailRef={emailRef}
        />
      </div>
    </div>
  )
}
